// Generates up to 3 contextual quick-reply prompt strings for the session opener.
// Rule-based (no AI call) — derives suggestions from discipline gaps, ACWR, and race calendar.

import { getSportProfile } from './sportProfile';

// Return YYYY-MM-DD in local time — avoids UTC drift when mixing
// new Date() with toISOString() (which is always UTC).
function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * @param {Array}  recentWorkouts   Full recent workout array (180 days)
 * @param {Object} coachingMemory   Coaching memory object (must have .races)
 * @param {string} sportFocus       e.g. 'triathlon', 'run', 'bike', 'swim', 'duathlon', 'aquabike'
 * @returns {string[]}              Up to 3 suggestion strings
 */
export function computeSessionSuggestions(recentWorkouts = [], coachingMemory = {}, sportFocus = 'triathlon') {
  const suggestions = [];
  const now = new Date();

  const profile = getSportProfile(sportFocus);
  // Only check disciplines relevant to this athlete's sport
  const DISCIPLINES = ['swim', 'bike', 'run'].filter(d => profile[d]);

  // ── 1. Discipline gap (14+ days without a relevant discipline) ─────────────
  // Only applies to multi-sport athletes with enough history (≥14 days of data)
  const oldestWorkout = recentWorkouts.reduce((min, w) => {
    const d = new Date(w.date);
    return (!min || d < min) ? d : min;
  }, null);
  const historyDays = oldestWorkout ? Math.round((now - oldestWorkout) / 86400000) : 0;

  if (DISCIPLINES.length > 1 && historyDays >= 14) {
    const lastSeen = {};
    recentWorkouts.forEach(w => {
      const type = (w.type || '').toLowerCase();
      if (!DISCIPLINES.includes(type)) return;
      const d = new Date(w.date);
      if (!lastSeen[type] || d > lastSeen[type]) lastSeen[type] = d;
    });

    let mostNeglected = null;
    let mostDays = 0;
    DISCIPLINES.forEach(type => {
      if (!lastSeen[type]) {
        // Never done in entire history window — only flag if history is substantial
        if (historyDays >= 21) {
          mostNeglected = type;
          mostDays = Infinity;
        }
      } else {
        const days = Math.round((now - lastSeen[type]) / 86400000);
        if (days >= 14 && days > mostDays) {
          mostNeglected = type;
          mostDays = days;
        }
      }
    });

    if (mostNeglected) {
      const label = mostNeglected === 'swim' ? 'swimming' : mostNeglected === 'bike' ? 'cycling' : 'running';
      const days = isFinite(mostDays) ? mostDays : null;
      suggestions.push(
        days
          ? `It's been ${days} days since my last ${label} session — what should I know before jumping back in?`
          : `I haven't done any ${label} yet — how should I approach my first session?`
      );
    }
  }

  // ── 2. ACWR check (re-derive ATL/CTL from workouts) ───────────────────────
  const k42 = 1 - Math.exp(-1 / 42);
  const k7  = 1 - Math.exp(-1 / 7);
  const tssByDay = {};
  recentWorkouts.forEach(w => {
    const d = w.date?.slice(0, 10);
    if (d) tssByDay[d] = (tssByDay[d] || 0) + (w.tss || 0);
  });
  let ctl = 0, atl = 0;
  for (let i = 41; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const tss = tssByDay[localDateStr(d)] || 0;
    ctl = ctl + (tss - ctl) * k42;
    atl = atl + (tss - atl) * k7;
  }
  const acwr = ctl > 1 ? atl / ctl : 0;

  if (ctl > 1) {
    if (acwr > 1.3) {
      suggestions.push(`My training load is quite high right now — am I at risk of overreaching or injury?`);
    } else if (acwr < 0.6) {
      suggestions.push(`I've been training less than usual — how should I build back safely without losing fitness?`);
    } else if (ctl - atl > 10) {
      suggestions.push(`My form is looking good — is this a good time to do a key quality session?`);
    }
  }

  // ── 3. Race calendar ──────────────────────────────────────────────────────
  // Only surface UPCOMING races — completed/DNF/DNS entries would produce
  // nonsense chips like "your PB race is in -12 days".
  const upcoming = (coachingMemory.races || [])
    .filter(r => {
      const status = r?.status || 'upcoming';
      if (status !== 'upcoming') return false;
      if (!r.date) return false;
      return new Date(r.date) >= now;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  if (upcoming.length > 0) {
    const race = upcoming[0];
    const daysUntil = Math.round((new Date(race.date) - now) / 86400000);
    const name = (race.name || race.distance || 'my next race').substring(0, 40);
    if (daysUntil <= 7) {
      suggestions.push(`${name} is in ${daysUntil} day${daysUntil === 1 ? '' : 's'} — what's my race week checklist?`);
    } else if (daysUntil <= 21) {
      suggestions.push(`${name} is in ${daysUntil} days — is my taper on track?`);
    } else if (daysUntil <= 60) {
      suggestions.push(`${name} is ${daysUntil} days away — what should I focus on this week?`);
    } else {
      suggestions.push(`What's the priority in training right now to peak for ${name}?`);
    }
  } else {
    suggestions.push(`I don't have a race on my calendar — how should I structure my training right now?`);
  }

  return suggestions.slice(0, 3);
}
